import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import MainLayout from "./layouts/MainLayout";
import { FIXED_HEADER_OFFSET } from "./layouts/headerLayout";
import { getBatchResultsApi, getBatchStatusApi } from "../api/enrichmentApi";
import { classifyLeadRow, isLeadRun } from "../container/Enrichment/leadOutcome";
import useHomeEnrichmentReviewEntry, { readReviewEntry } from "../hooks/useHomeEnrichmentReviewEntry";

/** Historical run membership comes from status, never today's search or workspace. */
export default function HomeExecutionReview() {
  const location = useLocation();
  const navigate = useNavigate();
  const entry = readReviewEntry(location.state);
  const executionId = entry.executionId;
  const [loaded, setLoaded] = useState(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const heading = useRef(null);
  useHomeEnrichmentReviewEntry(location.state, loaded !== null);

  useEffect(() => {
    let active = true;
    setLoaded(null);
    setError(false);
    Promise.all([getBatchStatusApi(executionId), getBatchResultsApi(executionId)])
      .then(([statusResponse, resultResponse]) => {
        const status = statusResponse?.data;
        const results = resultResponse?.data;
        if (statusResponse?.status !== 200 || resultResponse?.status !== 200
            || status?.batch_id !== executionId || results?.batch_id !== executionId
            || !Array.isArray(status.items) || !Array.isArray(results.results)
            || status.items.length !== status.total) throw new Error("Incomplete run");
        const byId = new Map(results.results.map((row) => [row.id, row]));
        const rows = status.items.map((item) => {
          const result = item.result_id ? byId.get(item.result_id) : null;
          if (item.result_id && (!result || result.company_id !== item.company_id || result.batch_id !== executionId)) {
            throw new Error("Referenced history is unavailable");
          }
          const lead = isLeadRun({ ...result, lead_outcome: item.lead_outcome })
            ? classifyLeadRow({ leadOutcome: item.lead_outcome, derivedColumns: result?.derived_columns }) : null;
          return { ...item, result, lead };
        });
        if (active) setLoaded({ status: status.status, rows });
      }).catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [executionId, attempt]);

  useEffect(() => { if (loaded) heading.current?.focus(); }, [loaded]);
  const title = loaded?.rows.find((row) => row.result?.query)?.result.query || entry.focusColumnKey || "Enrichment results";
  const reload = () => { setLoaded(null); setAttempt((value) => value + 1); };

  return <MainLayout><Box sx={{ pt: FIXED_HEADER_OFFSET, px: { xs: 2, md: 4 }, pb: 4, minWidth: 0 }}>
    <Typography variant="h5" component="h1" ref={heading} tabIndex={-1}>{title}</Typography>
    <Typography sx={{ my: 1 }}>Original run results</Typography>
    <Button onClick={() => navigate(location.pathname, { replace: true, state: null })}>Return to search</Button>
    {!loaded && !error && <Box role="status"><CircularProgress size={20} /> Loading original results…</Box>}
    {error && <Alert severity="error" action={<Button onClick={reload}>Retry</Button>}>Original results are unavailable. Please retry to view this run.</Alert>}
    {loaded && <>
      <Typography sx={{ my: 2 }}>Run status: {loaded.status}</Typography>
      {loaded.status !== "completed" && loaded.status !== "failed" && loaded.status !== "cancelled" && <Button onClick={reload}>Refresh results</Button>}
      {loaded.rows.length === 0 ? <Typography>No companies were attempted in this run.</Typography> :
        <Box sx={{ overflowX: "auto", width: "100%", "& th, & td": { p: 1.5, textAlign: "left", verticalAlign: "top", overflowWrap: "anywhere" } }}>
          <table aria-label="Original enrichment results" style={{ width: "100%", minWidth: 480, tableLayout: "fixed" }}>
            <thead><tr><th>Company</th><th>{title}</th><th>Status</th></tr></thead>
            <tbody>{loaded.rows.map((row) => <tr key={row.company_id}>
              <td>{row.result?.company_snapshot?.name || row.company_id}</td>
              <td>{row.lead ? (row.lead.result || row.lead.error) : row.result ? (row.result.answer?.answer?.summary || "No result returned") : (row.error || (row.status === "done" ? "No result returned" : "No result available"))}</td>
              <td>{row.lead?.title || row.status}</td>
            </tr>)}</tbody>
          </table>
        </Box>}
    </>}
  </Box></MainLayout>;
}
